export const seoAudit = {
  title: '360 Audit',
  badge: 'Technical SEO',
  form: {
    title: '360 Audit',
    subtitle: 'Analyze your website for technical SEO issues: meta tags, broken links, structured data, images and more.',
    urlLabel: 'Website URL',
    urlPlaceholder: 'https://yourcompany.com',
    advancedConfig: 'Advanced configuration',
    maxDepth: 'Max depth',
    maxDepthTooltip: 'Maximum crawl depth from the starting URL (1-10).',
    maxUrls: 'Max URLs',
    maxUrlsTooltip: 'Maximum number of URLs to crawl. Leave empty to use crawler default.',
    maxUrlsPlaceholder: 'Default (no limit)',
    jsRendering: 'JavaScript rendering (Chromium)',
    jsRenderingWarning: 'Enabling JS rendering uses a headless Chromium browser for each page, which can increase crawl time by up to 5×.',
    submitButton: 'Start SEO audit',
    submitting: 'Starting audit...',
    includePatterns: 'Include URL patterns',
    includePatternsPlaceholder: '/blog/, /products/',
    includePatternsTooltip: 'Only crawl URLs matching these patterns. Leave empty to crawl the entire site.',
    excludePatterns: 'Exclude URL patterns',
    excludePatternsPlaceholder: '/admin/, /wp-json/',
    excludePatternsTooltip: 'Skip URLs matching these patterns during crawling.',
    includeExtensions: 'Include file extensions',
    includeExtensionsPlaceholder: 'html, php',
    includeExtensionsTooltip: 'Only crawl files with these extensions.',
    excludeExtensions: 'Exclude file extensions',
    excludeExtensionsPlaceholder: 'pdf, zip, mp4',
    excludeExtensionsTooltip: 'Skip files with these extensions during crawling.',
    userAgent: 'User agent',
    userAgentPlaceholder:
      'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36',
    userAgentTooltip: 'Custom user-agent header sent with each request. Useful for sites that block default crawlers.',
    userAgentPreset: {
      placeholder: 'Pick a preset…',
      custom: 'Custom (type your own)',
      chromeLinux: 'Chrome — Desktop (Linux) · recommended',
      chromeWindows: 'Chrome — Desktop (Windows)',
      chromeMacos: 'Chrome — Desktop (macOS)',
      chromeMobile: 'Chrome — Mobile (Android)',
      safariIphone: 'Safari — Mobile (iPhone)',
      googlebotDesktop: 'Googlebot — Desktop',
      googlebotMobile: 'Googlebot — Smartphone',
    },
    crawlDelay: 'Crawl delay (seconds)',
    crawlDelayTooltip: 'Delay between requests in seconds. Increase for sites that rate-limit.',
    concurrency: 'Concurrency',
    concurrencyTooltip: 'Number of concurrent requests. Lower values are more polite to the server.',
    allowCookies: 'Allow cookies',
    allowCookiesTooltip: 'Accept and send cookies during crawling. Required for some authenticated or session-based sites.',
    respectRobotsTxt: 'Respect robots.txt',
    respectRobotsTxtTooltip: 'Follow robots.txt directives. Disable to crawl all pages regardless of robots.txt rules.',
    timeout: 'Request timeout (ms)',
    timeoutTooltip: 'Maximum time to wait for each page response.',
    retries: 'Retries',
    retriesTooltip: 'Number of retry attempts for failed requests.',
    enableProxy: 'Enable proxy',
    enableProxyTooltip: 'Route crawl requests through the configured proxy server.',
    jsWaitTime: 'JS wait time (ms)',
    jsWaitTimeTooltip: 'Time to wait after page load for JavaScript to render content.',
    jsTimeout: 'JS timeout (ms)',
    jsTimeoutTooltip: 'Maximum time to wait for JavaScript rendering to complete.',
    discoverSitemaps: 'Discover sitemaps',
    discoverSitemapsTooltip: 'Automatically discover and use sitemap.xml to find additional pages.',
    targetKeywords: 'Target keywords',
    targetKeywordsPlaceholder: 'seo audit, technical seo',
    targetKeywordsTooltip: 'Enter target keywords to check keyword optimization across your site. The audit will verify keyword placement in titles, descriptions, headings, and URLs.',
    brandName: 'Brand name (optional)',
    brandNamePlaceholder: 'e.g. Acme Corp',
    brandNameTooltip: 'Override the auto-detected brand name for Wikipedia and Wikidata checks. Leave empty to auto-detect from your site\'s Organization schema or domain.',
    sectionFilters: 'URL Filters',
    sectionCrawler: 'Crawler Settings',
    sectionProxy: 'Proxy',
    sectionJsRendering: 'JavaScript Rendering',
    howToCheck: 'How to check:',
    probeButton: 'Analyze site',
    probeButtonLoading: 'Analyzing...',
  },
  probe: {
    bannerAppliedTitle: 'Recommended settings applied',
    bannerWarningsTitle: 'Warnings',
    revert: 'Revert',
    fieldBadge: 'Auto-detected',
    spaDetected_angular: 'Angular SPA detected — enabling JS rendering',
    spaDetected_react: 'React SPA detected — enabling JS rendering',
    spaDetected_next: 'Next.js (client-side) detected — enabling JS rendering',
    spaDetected_vue: 'Vue SPA detected — enabling JS rendering',
    spaDetected_unknown: 'Single-page app detected — enabling JS rendering',
    sitemapFound: 'Valid sitemap found',
    sitemapMalformed: 'Sitemap URL exists but returns HTML instead of XML — the server likely returns the homepage for missing paths',
    sitemapNotFound: 'No sitemap found at the usual paths — disabling sitemap discovery to avoid pointless requests',
    robotsBlocksAll: 'robots.txt disallows all crawlers — the crawl may return few pages',
    robotsCrawlDelay: 'robots.txt sets a crawl delay — the audit will take longer than usual',
    botProtection_cloudflare:
      'Cloudflare challenge detected — enabling JS rendering with a realistic browser user agent to bypass it',
    botProtection_akamai:
      'Akamai Bot Manager detected — enabling JS rendering with a realistic browser user agent. May still fail from datacenter IPs (residential proxy may be required).',
    botProtection_forbidden:
      'Site returned 403 Forbidden — enabling JS rendering with a realistic browser user agent to look like a real Chrome',
    botProtection_rate_limit:
      'Site returned 429 Too Many Requests — enabling JS rendering and consider lowering concurrency',
    botProtectionUserAgent: 'Setting a realistic Chrome user agent — bot-protected sites reject default crawler UAs',
    botProtectionRobots:
      'Disabling robots.txt — bot-protected sites usually block robots.txt itself, so there is nothing to honor',
    unreachable: 'Could not reach the site — check the URL and try again',
    noIssuesFound: 'No issues detected — default settings look good for this site',
  },
  progress: {
    pending: 'Preparing...',
    crawling: 'Crawling in progress...',
    processing: 'Processing results...',
    analyzing: 'Analyzing',
    pagesCrawled: 'pages crawled',
    discovered: 'discovered',
    urlLimit: 'Limit',
    urlLimitUnlimited: 'Unlimited',
    substeps: {
      geoResearch: 'Researching the brand footprint',
      geo: 'Scoring AI presence',
      competitorCensus: 'Surveying the competitive market',
      presence: 'Analyzing competitive presence',
      presenceResearch: 'Researching competitors',
      presenceClassify: 'Classifying competitors',
      presenceFormat: 'Organizing competitor data',
      presenceSynthesis: 'Synthesizing the comparison',
      presenceEnrichment: 'Measuring confirmed competitors',
      keywordGap: 'Cross-checking keywords against competitors',
      expressBenchmark: 'Benchmarking against market competitors',
      aiChecks: 'Reviewing content with AI',
      aiDocs: 'Drafting the documentation',
      report: 'Building the report',
    },
    waitMessage: 'Crawling takes ~5 minutes per 1,000 URLs. Analysis and documentation generation adds another ~5 minutes.',
    steps: {
      discovering: 'Discovering pages',
      discoveringDesc: 'Scanning sitemaps and finding pages to crawl...',
      crawling: 'Crawling pages',
      crawlingDesc: 'Fetching and analyzing page content...',
      analyzing: 'Analyzing results',
      analyzingDesc: 'Running SEO checks and detecting issues...',
      generatingDocs: 'Generating documentation',
      generatingDocsDesc: 'Creating actionable recommendations with AI...',
      geoPresence: 'GEO & Presence analysis',
      geoPresenceDesc: 'Analyzing brand visibility in AI search and social platforms...',
      confirmCompetitors: 'Review competitors',
      confirmCompetitorsDesc: 'Confirm the AI-detected competitors before generating your report.',
      refineCompetitors: 'Measuring confirmed competitors',
      refineCompetitorsDesc: 'Domain metrics for the competitors you confirmed.',
      keywordGap: 'Analyzing keyword gaps',
      keywordGapDesc: 'Finding keywords your competitors rank for that you don\'t...',
      generatingReport: 'Generating 360° report',
      generatingReportDesc: 'Synthesizing all data into your final report...',
    },
    awaitingConfirmation: 'Your input is needed',
    awaitingConfirmationDesc: 'Review and confirm the competitors before we generate your report.',
  },
  report: {
    headerSubtitle: '360 Audit Report',
    radar: {
      score: 'Score',
      categories: {
        indexing: 'Indexing',
        onPage: 'On-Page',
        schema: 'Structured Data',
        links: 'Links',
        images: 'Images',
        contentStructure: 'Content',
        eeat: 'E-E-A-T',
        keywords: 'Keywords',
        external: 'External',
      },
    },
    pagesAnalyzed: '{{count}} pages analyzed',
    print: 'Print',
    regenerateDocs: 'Regenerate docs',
    scoreOverview: 'Score Overview',
    detailedResults: 'Detailed Results',
    backToAudits: 'Back to audits',
    crawlBlocked: {
      title: 'Crawl partially blocked',
      detail:
        '{{blocked}} of {{total}} pages ({{pct}}%) returned HTTP 429 — typically a bot-protection challenge (e.g. Cloudflare). Content checks were evaluated only on the pages that loaded successfully, so some results may be incomplete. Re-run with a proxy enabled or a lower crawl speed for full coverage.',
    },
    newAudit: 'New Audit',
  },
  summary: {
    score: 'Score',
    passed: 'Passed',
    failed: 'Failed',
    warnings: 'Warnings',
    pages: 'Pages',
  },
  sections: {
    indexing: {
      title: 'Indexing',
      description: 'Checks status codes, robots meta tags, canonical URLs, redirects and indexing directives for your site.',
    },
    onPage: {
      title: 'SEO On-Page',
      description: 'Analysis of meta titles, descriptions, H1 tags, lang attributes and content duplication.',
    },
    schema: {
      title: 'Structured Data',
      description: 'Review of Schema.org, JSON-LD, Article, FAQ and structured data to improve search engine visibility.',
    },
    links: {
      title: 'Links',
      description: 'Analysis of internal, external links, orphan pages, redirect loops and link depth.',
    },
    images: {
      title: 'Images',
      description: 'Review of alt text coverage, file sizes, image weight per page and descriptive filenames.',
    },
    contentStructure: {
      title: 'Content Structure',
      description: 'Heading hierarchy, thin content, page weight, load times and content structure analysis.',
    },
    eeat: {
      title: 'E-E-A-T',
      description: 'Experience, Expertise, Authoritativeness, and Trustworthiness signals: about page, contact info, author attribution, privacy policy, and social proof.',
    },
    keywordOptimization: {
      title: 'Keyword Optimization',
      description: 'Analysis of target keyword placement in URLs, titles, meta descriptions, H1 and H2 headings across your site.',
    },
    externalPresence: {
      title: 'External Presence',
      description: 'Checks your site\'s visibility across search engines, knowledge bases, and AI systems.',
    },
    manualChecklist: {
      title: 'Manual Review Checklist',
      description: 'These items require manual review and do not affect the automated audit score. Expand each item for guidance on how to check.',
      completed: 'completed',
    },
  },
  checksTable: {
    check: 'Check',
    status: 'Status',
    count: 'Count',
    statusLabels: {
      pass: 'Passed',
      fail: 'Failed',
      warning: 'Warning',
      na: 'N/A',
    },
  },
  documentation: {
    problem: 'Problem',
    technicalSolution: 'Technical Solution',
    clientCopy: 'Client Copy',
    priority: 'Priority',
    copyToClipboard: 'Copy',
    copied: 'Copied!',
    priorityLabels: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
  },
  history: {
    title: 'Audit History',
    toggleUpload: 'Toggle CSV upload',
    loading: 'Loading history...',
    empty: 'No previous audits',
    searchLabel: 'Search audits by URL',
    searchPlaceholder: 'Search by URL...',
    statusLabels: {
      completed: 'Completed',
      crawling: 'Crawling',
      processing: 'Processing',
      pending: 'Pending',
      failed: 'Failed',
      analyzing_geo: 'GEO Analysis',
      analyzing_presence: 'Presence Analysis',
      awaiting_confirmation: 'Awaiting Review',
      analyzing_presence_targeted: 'Re-analyzing Competitors',
      analyzing_keyword_gaps: 'Keyword Gaps',
      analyzing_benchmark: 'Express Benchmark',
      generating_report: 'Generating Report',
    },
    pagination: {
      prev: 'Previous page',
      next: 'Next page',
      pageOf: 'Page {{page}} of {{total}}',
    },
    upload: {
      invalidFileType: 'Please select a CSV file',
      uploadFailed: 'Upload failed',
      title: 'Upload LibreCrawl CSV export',
      uploading: 'Uploading…',
      retry: 'Retry',
      uploadCsv: 'Upload CSV',
    },
  },
  error: {
    title: 'Audit error',
    defaultMessage: 'An unexpected error occurred',
    reportFailed:
      'The report could not be generated. Everything the audit measured is saved, so retrying only writes the report again.',
    auditFailed: 'The audit could not be completed. Start a new audit to try again.',
    retryReport: 'Retry the report',
    retryingReport: 'Retrying the report…',
    backToAudits: 'Back to audits',
    resumeFailed: 'The report could not be restarted. Try again in a moment.',
    technicalDetail: 'Technical detail: {{message}}',
  },
  competitors: {
    title: 'Confirm competitors',
    subtitle: 'The AI identified these competitors from your brand\'s presence analysis. Review and edit before generating the report.',
    namePlaceholder: 'Company name',
    urlPlaceholder: 'https://competitor.com',
    reasonLabel: 'AI reason',
    typeLabel: 'Type',
    typeDirect: 'Direct',
    typeIndirect: 'Indirect',
    confirmButton: 'Confirm & generate report',
    confirming: 'Generating report...',
    addButton: 'Add competitor',
    removeLabel: 'Remove competitor',
    sourceBoth: 'Search data + market research',
    sourceAgentic: 'Market research',
    sourceDataForSeo: 'Search data',
    tierLeader: 'Category leader',
    tierPeer: 'Comparable size',
    tierNational: 'National operator',
    evidenceLabel: 'Source',
    evidenceOwnSite: 'Source: own site',
    consolidatedWith: 'Same operator as',
    confirmFailed: 'Confirmation failed. Please try again.',
    emptySetHint: 'Add at least one competitor to confirm.',
  },
  gate: {
    enrichmentFailed: 'The measurement failed. Review the competitors and confirm again.',
  },
  report360: {
    title: '360° Report',
    technicalSeo: 'Technical SEO',
    downloadPdf: 'Download PDF',
    downloadFailed: 'PDF download failed. Please try again.',
    regeneratePdf: 'Regenerate PDF',
    regenerateFailed: 'PDF regeneration failed. Please try again.',
    crawl: 'Crawl',
    on: 'On',
    off: 'Off',
    documentFrameTitle: 'SEO diagnostic document',
    documentLoading: 'Loading the diagnostic document',
    documentViewTechnical: 'Open the technical analysis',
    documentRetry: 'Try again',
    documentErrorTitle: 'We could not load the document',
    documentErrorDescription: 'Something went wrong while fetching the diagnostic. Try again, or go through to the technical analysis.',
    documentUnavailableTitle: 'This audit has no diagnostic document',
    documentUnavailableDescription: 'It finished before the document existed, or its copy could not be written. It can be written now from what the audit already measured, without crawling the site again.',
    downloadHtml: 'Download HTML',
    documentWarningTitle: 'Read this before you send it',
    documentWarningBody: 'The copy broke a style rule the checks could not settle. Most often it is a trend the audit did not measure. Check the wording before forwarding it.',
    documentRegenerate: 'Write the diagnostic',
    documentRegenerating: 'Writing it…',
    documentRegenerateFailed: 'The diagnostic could not be written. Try again in a moment.',
  },
  export: {
    json: 'JSON',
    csv: 'CSV',
    urlsCsv: 'URLs (CSV)',
    pdf: 'PDF',
    pdfSummary: 'Summary PDF',
    sectionPdf: 'Download PDF',
    sectionPdfDownloading: 'Downloading...',
  },
} as const
